import { useState } from "react";
import { perfectDayExperiences } from "@/data/resort";
import { cn } from "@/lib/utils";
import { Eyebrow, MaskReveal, Reveal } from "../reveal";

export function PerfectDay() {
  const [active, setActive] = useState(0);

  return (
    <section
      id="perfect-day"
      aria-labelledby="perfect-day-heading"
      className="bg-background py-16 md:py-20"
    >
      <div className="shell">
        <Reveal>
          <Eyebrow>What Kind of Day Are You Having?</Eyebrow>
        </Reveal>
        <h2 id="perfect-day-heading" className="display-lg mt-6 max-w-[16ch]">
          <MaskReveal>Choose Your Auburn</MaskReveal>
          <MaskReveal delay={0.08}>Adventure</MaskReveal>
        </h2>
        <Reveal delay={0.1}>
          <p className="mt-6 max-w-2xl text-sm leading-relaxed text-muted-foreground">
            These are inspirational sample experiences — not exact schedules. Confirm attraction
            hours, conditions and availability before you go.
          </p>
        </Reveal>
      </div>

      {/* Desktop cinematic panels */}
      <div className="shell mt-8 hidden h-[70vh] gap-2 md:flex">
        {perfectDayExperiences.map((experience, i) => {
          const isActive = active === i;
          return (
            <button
              key={experience.id}
              type="button"
              onMouseEnter={() => setActive(i)}
              onFocus={() => setActive(i)}
              className={cn(
                "relative overflow-hidden text-left transition-all duration-700 ease-[cubic-bezier(0.22,1,0.36,1)]",
                isActive ? "flex-[2.2]" : "flex-1",
              )}
              aria-pressed={isActive}
            >
              <img
                src={experience.image}
                alt={experience.alt}
                width={1400}
                height={1600}
                loading="lazy"
                decoding="async"
                className={cn(
                  "absolute inset-0 size-full object-cover transition-transform duration-1000",
                  isActive ? "scale-105" : "scale-100",
                )}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-forest-deep/90 via-forest-deep/35 to-forest-deep/10" />
              <div className="relative flex h-full flex-col justify-end p-6 text-primary-foreground lg:p-8">
                <h3 className="font-display mt-3 text-3xl tracking-tight lg:text-4xl">
                  {experience.title}
                </h3>
                <div
                  className={cn(
                    "overflow-hidden transition-all duration-500",
                    isActive ? "mt-5 max-h-60 opacity-100" : "max-h-0 opacity-0",
                  )}
                >
                  <ul className="space-y-2 text-sm text-primary-foreground/80">
                    {experience.beats.map((beat) => (
                      <li key={beat.when}>
                        <span className="eyebrow text-accent">{beat.when}</span>
                        <span className="mt-1 block">{beat.what}</span>
                      </li>
                    ))}
                  </ul>
                  <span className="eyebrow mt-5 inline-flex text-primary-foreground">
                    {experience.cta}
                  </span>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Mobile stacked cards */}
      <div className="shell mt-10 space-y-5 md:hidden">
        {perfectDayExperiences.map((experience, i) => (
          <Reveal key={experience.id} delay={i * 0.05}>
            <article className="relative overflow-hidden">
              <img
                src={experience.image}
                alt={experience.alt}
                width={1200}
                height={1400}
                loading="lazy"
                decoding="async"
                className="aspect-[4/5] w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-forest-deep/90 via-forest-deep/35 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 p-6 text-primary-foreground">
                <h3 className="font-display mt-3 text-3xl tracking-tight">{experience.title}</h3>
                <ul className="mt-5 space-y-2 text-sm text-primary-foreground/80">
                  {experience.beats.map((beat) => (
                    <li key={beat.when}>
                      <span className="eyebrow text-accent">{beat.when}</span>
                      <span className="mt-1 block">{beat.what}</span>
                    </li>
                  ))}
                </ul>
                <span className="eyebrow mt-5 inline-flex">{experience.cta}</span>
              </div>
            </article>
          </Reveal>
        ))}
      </div>
    </section>
  );
}
